import { Fragment, useEffect, useMemo } from 'react';
import custom from '~/assets/icons/custom.svg';
import glassTinting from '~/assets/icons/glass_tinting.svg';
import paint from '~/assets/icons/paint_2.svg';
import reset from '~/assets/icons/reset_2.svg';
import { PanelControls } from '~/components/ui/PanelControls';
import { rage, useInputAction, useObservable } from '~/rage';
import {
	InputAction,
	InputKey,
	VehicleTuningCategory,
	WalletType,
} from '~/shared';
import { useBuy, useResetTuning, useTuningCategories } from '../../hooks';
import {
	setActiveIndex,
	setActiveIndexNext,
	setActiveIndexPrev,
	setActiveStage,
	setActiveTuningCategory,
	setActiveTuningSubcategory,
	setMaxIndex,
	TuningStudioStage,
	useLayoutDispatch,
	useLayoutSelector,
} from '../../redux';
import { Categories } from '../Categories';
import { colorSelectItems } from '../ColorSelect/ColorSelect.model';
import { MenuItem } from '../MenuItem';
import { CATEGORY_ICONS, getResetTuningIndex } from './Main.model';
import { StyledContainer, StyledControlsContainer } from './Main.styled';

const Main = () => {
	const dispatch = useLayoutDispatch();
	const activeIndex = useLayoutSelector(
		(state) => state.tuningStudio.activeIndex,
	);
	const {
		shopCategories,
		shopItems,
		nonExteriorCategories,
		wheelSettingsRanges,
	} = useTuningCategories();
	const modalState = useObservable(rage.modal.getCurrentModal());
	const interfaceHidden = useLayoutSelector(
		(state) => state.tuningStudio.interfaceHidden,
	);
	const { handleBuy } = useBuy();
	const { confirmReset } = useResetTuning();

	const categoryItemCounts = useMemo(() => {
		const counts: Record<VehicleTuningCategory, number> = {} as Record<
			VehicleTuningCategory,
			number
		>;

		shopCategories.forEach((category) => {
			if (category.id === VehicleTuningCategory.WHEEL_SETTINGS) {
				if (wheelSettingsRanges) {
					counts[category.id] = 0;
				}
			} else {
				const categoryItems = shopItems.filter(
					(e) => e.categoryId === category.id,
				);
				if (categoryItems.length > 0) {
					counts[category.id] = categoryItems.length;
				}
			}
		});

		return counts;
	}, [shopCategories, wheelSettingsRanges, shopItems]);

	const exteriorCategoryCount = useMemo(() => {
		const rootIds = new Set<VehicleTuningCategory>([
			VehicleTuningCategory.RIMS,
			VehicleTuningCategory.WHEEL_SETTINGS,
		]);
		const groupsWithItems = new Set<string>();
		shopItems.forEach((item) => {
			if (rootIds.has(item.categoryId)) {
				return;
			}
			if (item.categoryId === VehicleTuningCategory.KITS) {
				groupsWithItems.add('__kits__');
			} else {
				groupsWithItems.add(item.categoryId);
			}
		});
		return groupsWithItems.size;
	}, [shopItems]);

	const isWheelSettingsSelected = useMemo(() => {
		return (
			activeIndex > 2 &&
			nonExteriorCategories[activeIndex - 3]?.id ===
				VehicleTuningCategory.WHEEL_SETTINGS
		);
	}, [activeIndex, nonExteriorCategories]);

	const resetIndex = getResetTuningIndex(nonExteriorCategories.length);
	const isResetSelected = activeIndex === resetIndex;

	const handleSelectCategory = (index: number) => {
		if (index === resetIndex) {
			dispatch(setActiveIndex(resetIndex));
			confirmReset();
			return;
		}
		if (index === 0) {
			dispatch(setActiveIndex(index));
			dispatch(setActiveStage(TuningStudioStage.PAINT));
		} else if (index === 1) {
			dispatch(setActiveIndex(0));
			dispatch(setActiveStage(TuningStudioStage.TINTING));
		} else if (index === 2) {
			dispatch(setActiveIndex(0));
			dispatch(setActiveStage(TuningStudioStage.EXTERIOR_MENU));
		} else if (
			nonExteriorCategories[index - 3]?.id ===
			VehicleTuningCategory.WHEEL_SETTINGS
		) {
			dispatch(setActiveIndex(index));
		} else {
			dispatch(setActiveIndex(0));
			dispatch(setActiveStage(TuningStudioStage.TUNING_MENU));
			dispatch(setActiveTuningCategory(nonExteriorCategories[index - 3].id));
			dispatch(setActiveTuningSubcategory(null));
		}
	};

	const leftControls = useMemo(
		() => [
			{
				text: 'Управление',
				keys: [InputKey.ArrowLeft, InputKey.ArrowRight],
			},
			isWheelSettingsSelected
				? {
						text: 'Купить',
						keys: [InputKey.Enter],
						onClick: () => handleBuy(WalletType.Cash),
				  }
				: isResetSelected
				? {
						text: 'Сбросить',
						keys: [InputKey.Enter],
						onClick: () => confirmReset(),
				  }
				: {
						text: 'Выбрать категорию',
						keys: [InputKey.Enter],
						onClick: () => handleSelectCategory(activeIndex),
				  },
			{
				text: 'Крутить камеру',
				keys: [InputKey.MouseRight],
			},
		],
		[activeIndex, isWheelSettingsSelected, isResetSelected, handleBuy],
	);

	const rightControls = [
		{
			text: 'Выйти',
			keys: ['Esc'],
			onClick: () => rage.vehicleTuningShop.exitMenu(),
		},
	];

	useInputAction(InputAction.CommonMenuNavLeft, () => {
		if (modalState || interfaceHidden) {
			return;
		}
		dispatch(setActiveIndexPrev());
	});

	useInputAction(InputAction.CommonMenuNavRight, () => {
		if (modalState || interfaceHidden) {
			return;
		}
		dispatch(setActiveIndexNext());
	});

	useInputAction(InputAction.CommonMenuAccept, () => {
		if (modalState || interfaceHidden) {
			return;
		}
		if (isWheelSettingsSelected) {
			handleBuy(WalletType.Cash);
			return;
		}
		handleSelectCategory(activeIndex);
	});

	useInputAction(InputAction.CommonMenuClose, () => {
		if (modalState || interfaceHidden) {
			return;
		}
		rage.vehicleTuningShop.exitMenu();
	});

	useEffect(() => {
		dispatch(setMaxIndex(resetIndex));
	}, [resetIndex]);

	return (
		<Fragment>
			<StyledContainer>
				<Categories
					name="Все категории"
					activeIndex={activeIndex}
					totalCount={resetIndex + 1}
					handlePrev={() => dispatch(setActiveIndexPrev())}
					handleNext={() => dispatch(setActiveIndexNext())}
				>
					<MenuItem
						type="menu"
						name="Покрасочная"
						icon={paint}
						count={colorSelectItems.length}
						onClick={() => handleSelectCategory(0)}
						active={activeIndex === 0}
						id={`menuItem#0`}
					/>
					<MenuItem
						type="menu"
						name="Тонировка"
						icon={glassTinting}
						count={
							rage.vehiclePaintShop.getVehicleWindowTints().getValue().length
						}
						onClick={() => handleSelectCategory(1)}
						active={activeIndex === 1}
						id={`menuItem#1`}
					/>
					<MenuItem
						type="menu"
						name="Внешний тюнинг"
						icon={custom}
						count={exteriorCategoryCount}
						onClick={() => handleSelectCategory(2)}
						active={activeIndex === 2}
						id={`menuItem#2`}
					/>
					{nonExteriorCategories.map((category, index) => (
						<MenuItem
							type="menu"
							name={category.name}
							icon={CATEGORY_ICONS[category.id]}
							count={categoryItemCounts[category.id] || 0}
							key={category.id}
							onClick={() => handleSelectCategory(index + 3)}
							active={activeIndex === index + 3}
							id={`menuItem#${index + 3}`}
						/>
					))}
					<MenuItem
						type="menu"
						name="Сброс в сток"
						icon={reset}
						onClick={() => handleSelectCategory(resetIndex)}
						active={isResetSelected}
						id={`menuItem#${resetIndex}`}
					/>
				</Categories>
			</StyledContainer>
			<StyledControlsContainer>
				<PanelControls controls={leftControls} align="left" />
				<PanelControls controls={rightControls} align="right" />
			</StyledControlsContainer>
		</Fragment>
	);
};

export { Main };
